import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Select, Button, DatePicker, message } from 'antd';
import { useAttributes } from 'src/hooks/useProvideAttributes';
import { SelectWithPagination } from 'src/components/SelectWithPagination';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { textFrom } from 'src/utils/textFrom';

const { Option } = Select;

const attributeToOption = item => ({ value: item.key, label: item.label });

export const ProductAttribute = ({ departments, currentDepartment, value, onChange, onDelete, className, disabledDate, alreadyChosenValues, language }) => {
  const { t } = useTranslation();
  const text = textFrom('components.productAttribute', t);

  const { getAttributes, attributeCodes, fetchAttributeCodes } = useAttributes();
  const [attributeCodesToShowInSelect, setAttributeCodesToShowInSelect] = useState([]);
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [attributePagination, setAttributePagination] = useState({});

  useEffect(() => {
    fetchAttributeCodes(currentDepartment);
  }, [currentDepartment]);

  useEffect(async () => {
    try {
      if (attributeCodes?.length > 0) {
        const allDepartmentsDeclareVariants = [];
        departments.forEach(department => {
          allDepartmentsDeclareVariants.push(...department?.declareVariants);
        });

        const codesAlreadyIn = [];
        alreadyChosenValues.forEach(code => {
          code?.code && codesAlreadyIn.push(code.code.toLowerCase());
        });

        const newAttributeCodesToShowInSelect = attributeCodes?.filter((key) => {
          return !codesAlreadyIn.includes(key.toLowerCase()) && !allDepartmentsDeclareVariants.includes(key);
        }
        );
        setAttributeCodesToShowInSelect(newAttributeCodesToShowInSelect);
      }
    } catch (e) {
      message.error(text('attributesKO'));
    }
  }, [attributeCodes, departments, alreadyChosenValues]);

  const onLoadMore = useCallback(
    (page, language) => {
      return getAttributes(value.code, page, language?.language, currentDepartment);
    },
    [value.code, getAttributes, language, departments]
  );

  const getAttributeOptionsAtPage = useCallback(
    async (page) => {
      const res = await onLoadMore(page, language);
      setAttributeOptions(res?.data?.data?.items || []);
      setAttributePagination({ ...res?.data?.data });
    },
    [onLoadMore, language]
  );

  const onChangeCode = useCallback(
    (code) => {
      onChange({
        ...value,
        code,
        value: null
      });
    },
    [onChange, value]
  );

  const onChangeValue = useCallback(
    (v) => {
      onChange({
        ...value,
        value: v
      });
    },
    [onChange, value]
  );

  const getCorrectField = (value) => {
    switch (value.code) {
      case 'series_start_date':
      case 'series_end_date':
        return (
          <DatePicker
            getPopupContainer={trigger => trigger.parentElement}
            disabledDate={disabledDate}
            className='w-full'
            format='DD-MM-YYYY'
            onChange={onChangeValue}
            value={value.value || (value.key && moment(value.key)) || null}
            defaultValue={null}
            placeholder='DD-MM-YYYY'
          />
        );
      case 'series_id':
      default:
        return (
          <SelectWithPagination
            key={value.code}
            searchable={false}
            items={attributeOptions}
            getItemsAtPage={getAttributeOptionsAtPage}
            pagination={attributePagination}
            toOption={attributeToOption}
            defaultOptions={value.key && value.label ? [{ value: value.key, label: value.label }] : []}
            value={value.value ?? value.key}
            onChange={onChangeValue}
            placeholder={value.code === 'series_id' ? 'Series ID' : text('selectPlaceholder')}
          />
        );
    }
  };

  return (
    <Row gutter={[16, 16]} className={className}>
      <Col span={8}>
        <Select
          getPopupContainer={trigger => trigger.parentNode}
          value={value.code}
          onChange={onChangeCode}
          className='w-full'
          placeholder={text('selectPlaceholder')}
        >
          {attributeCodesToShowInSelect?.map((code) => (
            <Option key={code} value={code}>
              {code}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={8}>
        {getCorrectField(value)}
      </Col>
      <Col span={8} className='relative'>
        <div className='absolute bottom-0'>
          <Button
            className='uppercase delete-attribute-button'
            type='link'
            onClick={onDelete}
          >
            {text('removeAttribute')}
          </Button>
        </div>
      </Col>
    </Row>
  );
};
